/**
 * Compact cross-project recent-exports panel for the global Reporting page.
 *
 * Surfaces the last N CSV exports across all projects with a one-click
 * download for completed ones. Auto-refreshes every 5 s while any job is
 * still processing.
 */

import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileSpreadsheet, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useReportExports } from '@/hooks/useReportExports';
import type { ExportJobStatus } from '@/services/reports';

function statusBadgeClass(status: ExportJobStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-success/10 text-success border-success/20';
    case 'processing':
      return 'bg-info/10 text-info border-info/20';
    case 'failed':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'cancelled':
      return 'bg-muted text-muted-foreground border-muted';
    default:
      return 'bg-muted text-muted-foreground border-muted';
  }
}

const DATE_FMT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, DATE_FMT);
}

interface GlobalExportsPanelProps {
  limit?: number;
}

export function GlobalExportsPanel({ limit = 10 }: GlobalExportsPanelProps) {
  const { exports, isLoading, isRefetching, error, refetch } = useReportExports(
    { kind: 'global' },
    limit,
  );

  const handleRefresh = useCallback(() => refetch(), [refetch]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            Recent CSV exports
          </CardTitle>
          <CardDescription>
            Last {limit} exports across all projects. Completed jobs can be
            re-downloaded for up to 24 hours.
          </CardDescription>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5"
          onClick={handleRefresh}
          disabled={isRefetching}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {error ? (
          <p className="text-sm text-destructive">
            Failed to load exports{error instanceof Error ? `: ${error.message}` : '.'}
          </p>
        ) : isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : exports.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No recent exports.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {exports.map((item) => (
              <li
                key={item.job_id}
                className="flex items-center justify-between gap-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1 flex items-center gap-3">
                  <Badge
                    variant="outline"
                    className={`${statusBadgeClass(item.status)} text-xs`}
                  >
                    {item.status}
                  </Badge>
                  <Link
                    to={`/admin/projects/${item.project_id}/exports`}
                    className="font-mono text-xs truncate hover:underline"
                    title={item.project_id}
                  >
                    {item.project_id}
                  </Link>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(item.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {item.status === 'processing' && (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {item.scenarios_processed}/{item.total_scenarios}
                    </span>
                  )}
                  {item.status === 'completed' && item.download_url && (
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1"
                    >
                      <a
                        href={item.download_url}
                        download={`report-${item.job_id}.csv`}
                      >
                        <Download className="h-3 w-3" />
                        Download
                      </a>
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
